import React, { useEffect, useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
    getAllContentLibrary,
    addContentLibrary,
    updateContentLibrary,
    toggleContentLibraryStatus,
    getAdminProfile,
} from "../globals/Services.js";

import { authToken } from '../globals/Global';

const getAuthToken = () => authToken || localStorage.getItem("sessionToken");

const inferContentType = (file) => {
    if (!file) return "";
    const mime = file.type || "";
    const name = file.name.toLowerCase();
    if (mime.startsWith("video/") || name.endsWith(".mp4") || name.endsWith(".mov") || name.endsWith(".avi") || name.endsWith(".mkv") || name.endsWith(".webm")) return "video";
    if (mime.startsWith("image/") || name.endsWith(".jpg") || name.endsWith(".jpeg") || name.endsWith(".png") || name.endsWith(".gif") || name.endsWith(".webp") || name.endsWith(".svg")) return "image";
    if (mime.startsWith("audio/") || name.endsWith(".mp3") || name.endsWith(".wav") || name.endsWith(".ogg") || name.endsWith(".aac")) return "audio";
    if (mime === "application/pdf" || name.endsWith(".pdf")) return "pdf";
    if (mime.startsWith("text/") || name.endsWith(".txt") || name.endsWith(".md") || name.endsWith(".csv")) return "document";
    return "";
};

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;1,9..40,300&family=DM+Mono:wght@300;400&display=swap');

  :root {
    --bg: #f7f6f3;
    --surface: #ffffff;
    --border: #e8e5df;
    --border-strong: #d0ccc3;
    --text-primary: #1a1916;
    --text-secondary: #6b6760;
    --text-muted: #a09c97;
    --accent: #2d2d2a;
    --accent-hover: #1a1916;
    --success: #2d6a4f;
    --success-bg: #d8f3dc;
    --danger: #7d2327;
    --danger-bg: #fde8e8;
    --warning-bg: #fff8e1;
    --warning-text: #7d5a00;
    --radius: 8px;
    --radius-sm: 5px;
    --shadow: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);
    --shadow-md: 0 4px 12px rgba(0,0,0,0.07), 0 2px 4px rgba(0,0,0,0.04);
    --font: 'DM Sans', sans-serif;
    --mono: 'DM Mono', monospace;
    --transition: 150ms ease;
  }

  * { box-sizing: border-box; margin: 0; padding: 0; }

  .clm-root {
    font-family: var(--font);
    background: var(--bg);
    min-height: 100vh;
    padding: 40px 24px;
    color: var(--text-primary);
  }

  .clm-wrap {
    max-width: 960px;
    margin: 0 auto;
  }

  .clm-header {
    display: flex;
    align-items: baseline;
    gap: 12px;
    margin-bottom: 32px;
  }

  .clm-title {
    font-size: 22px;
    font-weight: 500;
    letter-spacing: -0.3px;
    color: var(--text-primary);
  }

  .clm-subtitle {
    font-size: 13px;
    color: var(--text-muted);
    font-weight: 300;
  }

  .clm-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    box-shadow: var(--shadow);
    padding: 28px;
    margin-bottom: 20px;
  }

  .clm-card-title {
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--text-muted);
    margin-bottom: 20px;
  }

  .clm-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 20px;
  }

  .clm-grid-full { grid-column: 1 / -1; }

  .clm-field label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 500;
    color: var(--text-secondary);
    margin-bottom: 6px;
    letter-spacing: 0.01em;
  }

  .clm-field-hint {
    font-size: 10px;
    font-weight: 400;
    font-family: var(--mono);
    color: var(--text-muted);
    background: var(--bg);
    border: 1px solid var(--border);
    padding: 1px 6px;
    border-radius: 3px;
    letter-spacing: 0.02em;
  }

  .clm-input,
  .clm-textarea {
    width: 100%;
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 9px 12px;
    font-family: var(--font);
    font-size: 14px;
    color: var(--text-primary);
    background: var(--bg);
    outline: none;
    transition: border-color var(--transition), box-shadow var(--transition);
    -webkit-appearance: none;
  }

  .clm-input:focus,
  .clm-textarea:focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(45,45,42,0.08);
    background: #fff;
  }

  .clm-textarea { resize: vertical; min-height: 88px; line-height: 1.5; }

  .clm-file-label {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 12px;
    border: 1px dashed var(--border-strong);
    border-radius: var(--radius-sm);
    cursor: pointer;
    font-size: 13px;
    color: var(--text-secondary);
    background: var(--bg);
    transition: border-color var(--transition), background var(--transition);
  }

  .clm-file-label:hover {
    border-color: var(--accent);
    background: #fff;
  }

  .clm-file-label input { display: none; }

  .clm-file-name {
    font-family: var(--mono);
    font-size: 12px;
    color: var(--accent);
  }

  .clm-actions { display: flex; gap: 10px; align-items: center; }

  .clm-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 9px 18px;
    font-family: var(--font);
    font-size: 13px;
    font-weight: 500;
    border-radius: var(--radius-sm);
    border: 1px solid transparent;
    cursor: pointer;
    transition: all var(--transition);
    letter-spacing: 0.01em;
    line-height: 1;
  }

  .clm-btn-primary {
    background: var(--accent);
    color: #fff;
    border-color: var(--accent);
  }
  .clm-btn-primary:hover { background: var(--accent-hover); }

  .clm-btn-outline {
    background: transparent;
    color: var(--text-secondary);
    border-color: var(--border-strong);
  }
  .clm-btn-outline:hover {
    background: var(--bg);
    color: var(--text-primary);
    border-color: var(--accent);
  }

  .clm-btn-sm {
    padding: 6px 12px;
    font-size: 12px;
    border-radius: 4px;
  }

  .clm-btn-danger {
    background: transparent;
    color: var(--danger);
    border-color: #f3c5c5;
  }
  .clm-btn-danger:hover { background: var(--danger-bg); }

  .clm-btn-success {
    background: transparent;
    color: var(--success);
    border-color: #b7e4c7;
  }
  .clm-btn-success:hover { background: var(--success-bg); }

  .clm-edit-banner {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    background: var(--warning-bg);
    border: 1px solid #f0d080;
    border-radius: var(--radius-sm);
    font-size: 12px;
    color: var(--warning-text);
    margin-bottom: 20px;
    font-weight: 500;
  }

  .clm-edit-id {
    font-family: var(--mono);
    background: #fff0b3;
    padding: 1px 6px;
    border-radius: 3px;
    font-size: 11px;
  }

  .clm-table-wrap { overflow-x: auto; }

  .clm-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13.5px;
  }

  .clm-table thead th {
    text-align: left;
    padding: 10px 14px;
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: var(--text-muted);
    border-bottom: 1px solid var(--border);
    white-space: nowrap;
  }

  .clm-table tbody tr {
    border-bottom: 1px solid var(--border);
    transition: background var(--transition);
  }
  .clm-table tbody tr:last-child { border-bottom: none; }
  .clm-table tbody tr:hover { background: var(--bg); }

  .clm-table td {
    padding: 13px 14px;
    color: var(--text-primary);
    vertical-align: middle;
  }

  .clm-id {
    font-family: var(--mono);
    font-size: 12px;
    color: var(--text-muted);
  }

  .clm-content-title {
    font-weight: 500;
    color: var(--text-primary);
  }

  .clm-desc {
    color: var(--text-secondary);
    font-size: 13px;
    max-width: 220px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
    .clm-cell {
    position: relative;
    max-width: 240px;
}

.clm-ellipsis {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: pointer;
    color: var(--text-secondary);
}

.clm-popover {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    z-index: 50;
    width: 340px;
    max-width: 420px;
    background: #fff;
    border: 1px solid var(--border);
    border-radius: 8px;
    box-shadow: var(--shadow-md);
    padding: 12px;
    font-size: 13px;
    line-height: 1.6;
    white-space: normal;
    color: var(--text-primary);
    word-break: break-word;
}

.clm-popover::before {
    content: "";
    position: absolute;
    top: -7px;
    left: 18px;
    border-left: 7px solid transparent;
    border-right: 7px solid transparent;
    border-bottom: 7px solid white;
}

.clm-popover::after {
    content: "";
    position: absolute;
    top: -8px;
    left: 17px;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 8px solid var(--border);
    z-index: -1;
}

  .clm-type-chip {
    display: inline-block;
    padding: 3px 9px;
    border-radius: 99px;
    font-size: 11px;
    font-weight: 500;
    font-family: var(--mono);
    background: var(--bg);
    border: 1px solid var(--border);
    color: var(--text-secondary);
  }

  .clm-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 9px;
    border-radius: 99px;
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.02em;
  }

  .clm-badge::before {
    content: '';
    width: 5px;
    height: 5px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .clm-badge-active { background: var(--success-bg); color: var(--success); }
  .clm-badge-active::before { background: var(--success); }
  .clm-badge-inactive { background: var(--danger-bg); color: var(--danger); }
  .clm-badge-inactive::before { background: var(--danger); }

  .clm-link {
    color: var(--text-secondary);
    font-size: 12px;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border-radius: 4px;
    border: 1px solid var(--border);
    transition: all var(--transition);
  }
  .clm-link:hover {
    color: var(--text-primary);
    border-color: var(--border-strong);
    background: var(--bg);
  }

  .clm-row-actions { display: flex; gap: 6px; align-items: center; }

  .clm-empty {
    text-align: center;
    padding: 48px 24px;
    color: var(--text-muted);
    font-size: 14px;
  }

  .clm-count {
    font-family: var(--mono);
    font-size: 11px;
    color: var(--text-muted);
    margin-left: auto;
  }

  .clm-card-header {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
  }

  @media (max-width: 640px) {
    .clm-grid { grid-template-columns: 1fr; }
    .clm-grid-full { grid-column: 1; }
    .clm-root { padding: 24px 16px; }
    .clm-card { padding: 20px; }
  }
`;

const UploadIcon = () => (
    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
    </svg>
);

const ExternalIcon = () => (
    <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
    </svg>
);

const PlusIcon = () => (
    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
);

const ContentLibraryManagement = () => {
    const [contentList, setContentList] = useState([]);
    const [adminUserId, setAdminUserId] = useState(null);
    const [formData, setFormData] = useState({
        contentId: "",
        title: "",
        description: "",
        contentType: "",
    });
    const [file, setFile] = useState(null);
    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [expandedCell, setExpandedCell] = useState(null);

    const fetchAdminProfile = async () => {
        try {
            const response = await axios.get(getAdminProfile, {
                headers: { Authorization: `Bearer ${getAuthToken()}` },
            });
            const profiles = Array.isArray(response.data) ? response.data : response.data?.data;
            const admin = profiles?.find((u) => u.roles === "ADMIN");
            const userId = admin?.id || admin?.userId;
            if (userId) setAdminUserId(userId);
        } catch (error) {
            console.error("Error fetching admin profile:", error);
        }
    };

    const fetchAllContent = async () => {
        try {
            const response = await axios.get(getAllContentLibrary, {
                headers: { Authorization: `Bearer ${getAuthToken()}` },
            });
            console.log(response.data, "response.data");
            if (response.data.status === "SUCCESS") {
                setContentList(response.data.data || []);
            }
        } catch (error) {
            console.error("Error fetching content:", error);
        }
    };

    useEffect(() => {
        fetchAdminProfile();
        fetchAllContent();
    }, []);

    useEffect(() => {

        const handleClick = () => {
            setExpandedCell(null);
        };

        window.addEventListener("click", handleClick);

        return () =>
            window.removeEventListener("click", handleClick);

    }, []);

    const handleChange = (e) =>
        setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleAddContent = async () => {
        try {
            const payload = new FormData();
            payload.append("title", formData.title);
            payload.append("description", formData.description);
            payload.append("contentType", formData.contentType);
            payload.append("userId", adminUserId);
            if (file) payload.append("file", file);

            if (thumbnailFile) {
                payload.append("contentThumbnail", thumbnailFile);
            }

            const response = await axios.post(addContentLibrary, payload, {
                headers: { Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "multipart/form-data" },
            });
            if (response.data.status === "SUCCESS") {
                toast.success("Content added successfully");
                resetForm();
                fetchAllContent();
            }
        } catch (error) {
            console.error("Error adding content:", error);
            toast.error("Failed to add content");
        }
    };

    const handleUpdateContent = async () => {
        try {
            const payload = new FormData();
            payload.append("contentId", formData.contentId);
            if (formData.title) payload.append("title", formData.title);
            if (formData.description) payload.append("description", formData.description);
            if (formData.contentType) payload.append("contentType", formData.contentType);
            payload.append("userId", adminUserId);
            if (file) payload.append("file", file);

            if (thumbnailFile) {
                payload.append("contentThumbnail", thumbnailFile);
            }

            const response = await axios.post(updateContentLibrary, payload, {
                headers: { Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "multipart/form-data" },
            });
            if (response.data.status === "SUCCESS") {
                toast.success("Content updated successfully");
                resetForm();
                fetchAllContent();
            }
        } catch (error) {
            console.error("Error updating content:", error);
            toast.error("Failed to update content");
        }
    };

    const handleToggleStatus = async (contentId, userId) => {
        try {
            const response = await axios.delete(toggleContentLibraryStatus, {
                headers: { Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json" },
                data: { contentId, userId },
            });
            if (response.data.status === "SUCCESS") {
                toast.success("Status updated successfully");
                fetchAllContent();
            }
        } catch (error) {
            console.error("Error updating status:", error);
            toast.error("Failed to update status");
        }
    };

    const handleEdit = (item) => {
        setIsEditMode(true);
        setFormData({
            contentId: item.id,
            title: item.title,
            description: item.description,
            contentType: item.contentType,
        });
    };

    const resetForm = () => {
        setFormData({ contentId: "", title: "", description: "", contentType: "" });
        setFile(null);
        setThumbnailFile(null);
        setIsEditMode(false);
    };

    return (
        <>
            <style>{styles}</style>
            <div className="clm-root">
                <div className="clm-wrap">

                    <div className="clm-header">
                        <h1 className="clm-title">Content Library</h1>
                        <span className="clm-subtitle">Manage and organise your content assets</span>
                    </div>

                    <div className="clm-card">
                        <div className="clm-card-title">
                            {isEditMode ? "Edit Content" : "New Content"}
                        </div>

                        {isEditMode && (
                            <div className="clm-edit-banner">
                                Editing entry
                                <span className="clm-edit-id">#{formData.contentId}</span>
                                — changes will overwrite existing values.
                            </div>
                        )}

                        <div className="clm-grid">
                            <div className="clm-field">
                                <label>Title</label>
                                <input
                                    className="clm-input"
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    placeholder="Enter a title…"
                                />
                            </div>

                            <div className="clm-field">
                                <label>
                                    Content Type
                                    <span className="clm-field-hint">auto-filled from file</span>
                                </label>
                                <input
                                    className="clm-input"
                                    type="text"
                                    name="contentType"
                                    value={formData.contentType}
                                    onChange={handleChange}
                                    placeholder="e.g. video, image…"
                                />
                            </div>

                            <div className="clm-field clm-grid-full">
                                <label>Description</label>
                                <textarea
                                    className="clm-textarea"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    placeholder="Brief description of the content…"
                                />
                            </div>

                            <div className="clm-field clm-grid-full">
                                <label>File</label>
                                <label className="clm-file-label">
                                    <UploadIcon />
                                    <span>{file ? <span className="clm-file-name">{file.name}</span> : "Choose a file to upload"}</span>
                                    <input
                                        type="file"
                                        onChange={(e) => {
                                            const picked = e.target.files[0] || null;
                                            setFile(picked);
                                            if (picked) {
                                                const inferred = inferContentType(picked);
                                                if (inferred) {
                                                    setFormData((prev) => ({ ...prev, contentType: inferred }));

                                                    // No thumbnail required for images
                                                    if (inferred === "image") {
                                                        setThumbnailFile(null);
                                                    }
                                                }
                                            }
                                        }}
                                    />
                                </label>
                            </div>

                            {formData.contentType === "video" && (
                                <div className="clm-field clm-grid-full">
                                    <label>Video Thumbnail</label>

                                    <label className="clm-file-label">
                                        <UploadIcon />

                                        <span>
                                            {thumbnailFile
                                                ? <span className="clm-file-name">{thumbnailFile.name}</span>
                                                : "Choose thumbnail image"}
                                        </span>

                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) =>
                                                setThumbnailFile(e.target.files[0] || null)
                                            }
                                        />
                                    </label>
                                </div>
                            )}
                        </div>

                        <div className="clm-actions">
                            {!isEditMode ? (
                                <button className="clm-btn clm-btn-primary" onClick={handleAddContent}>
                                    <PlusIcon /> Add Content
                                </button>
                            ) : (
                                <>
                                    <button className="clm-btn clm-btn-primary" onClick={handleUpdateContent}>
                                        Save Changes
                                    </button>
                                    <button className="clm-btn clm-btn-outline" onClick={resetForm}>
                                        Cancel
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="clm-card">
                        <div className="clm-card-header">
                            <span className="clm-card-title" style={{ marginBottom: 0 }}>All Content</span>
                            <span className="clm-count">{contentList.length} item{contentList.length !== 1 ? "s" : ""}</span>
                        </div>

                        <div className="clm-table-wrap">
                            <table className="clm-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Title</th>
                                        <th>Description</th>
                                        <th>Type</th>
                                        <th>Status</th>
                                        <th>File</th>
                                        <th>Thumbnail</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {contentList.length === 0 ? (
                                        <tr>
                                            <td colSpan={8}>
                                                <div className="clm-empty">No content found. Add your first entry above.</div>
                                            </td>
                                        </tr>
                                    ) : (
                                        contentList.map((item) => (
                                            <tr key={item.id}>
                                                <td><span className="clm-id">{item.id}</span></td>
                                                <td>
                                                    <div className="clm-cell">

                                                        <div
                                                            className="clm-ellipsis"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setExpandedCell(
                                                                    expandedCell === `title-${item.id}`
                                                                        ? null
                                                                        : `title-${item.id}`
                                                                )
                                                            }}
                                                        >
                                                            {item.title}
                                                        </div>

                                                        {expandedCell === `title-${item.id}` && (
                                                            <div className="clm-popover">
                                                                {item.title}
                                                            </div>
                                                        )}

                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="clm-cell">

                                                        <div
                                                            className="clm-ellipsis"
                                                            onClick={(e) => {
                                                                e.stopPropagation();

                                                                setExpandedCell(
                                                                    expandedCell === `desc-${item.id}`
                                                                        ? null
                                                                        : `desc-${item.id}`
                                                                );
                                                            }}
                                                        >
                                                            {item.description}
                                                        </div>

                                                        {expandedCell === `desc-${item.id}` && (
                                                            <div className="clm-popover">
                                                                {item.description}
                                                            </div>
                                                        )}

                                                    </div>
                                                </td>
                                                <td><span className="clm-type-chip">{item.contentType}</span></td>
                                                <td>
                                                    <span className={`clm-badge ${item.status === "ACTIVE" ? "clm-badge-active" : "clm-badge-inactive"}`}>
                                                        {item.status === "ACTIVE" ? "Active" : "Inactive"}
                                                    </span>
                                                </td>
                                                <td>
                                                    <a className="clm-link" href={item.contentUrl} target="_blank" rel="noreferrer">
                                                        View <ExternalIcon />
                                                    </a>
                                                </td>
                                                <td style={{ textAlign: "center" }}>
                                                    {item.contentThumbnail ? (
                                                        <a
                                                            className="clm-link"
                                                            href={item.contentThumbnail}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >
                                                            View <ExternalIcon />
                                                        </a>
                                                    ) : (
                                                        <span style={{ color: "#999" }}>—</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <div className="clm-row-actions">
                                                        <button className="clm-btn clm-btn-sm clm-btn-outline" onClick={() => handleEdit(item)}>
                                                            Edit
                                                        </button>
                                                        <button
                                                            className={`clm-btn clm-btn-sm ${item.status === "ACTIVE" ? "clm-btn-danger" : "clm-btn-success"}`}
                                                            onClick={() => handleToggleStatus(item.id, item.userId)}
                                                        >
                                                            {item.status === "ACTIVE" ? "Deactivate" : "Activate"}
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                </div>
            </div>

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </>
    );
};

export default ContentLibraryManagement;